import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Flame, Award, ShieldAlert, Sparkles, Check, Play, ChevronRight } from 'lucide-react';
import { AtomAvatar } from '@/components/AtomAvatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { calculateLevel, getProgressToNextLevel, getTotalXpForLevel } from '@/utils/levelCalculations';

export interface LessonCompleteProps {
  xpEarned: number;
  totalXP: number;
  accuracy: number; // 0-100
  perfectLesson: boolean;
  combo: number;
  streakDays: number;
  streakBroken: boolean;
  levelUp: boolean;
  newLevel: number;
  questsCompleted: Array<{ id: string; title: string; reward: number }>;
  gemsEarned: number;
  heartsRemaining: number; // 0-5
  onContinue: () => void; // final continue callback
  onReviewLesson: () => void; // review callback
}

function AnimatedCounter({ value, duration = 1.2 }: { value: number; duration?: number }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp: number | null = null;
    const step = (timestamp: number) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / (duration * 1000), 1);
      const easedProgress = progress * (2 - progress);
      setCount(Math.floor(easedProgress * value));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [value, duration]);

  return <>{count}</>;
}

export function LessonComplete({
  xpEarned,
  totalXP,
  accuracy,
  perfectLesson,
  combo,
  streakDays,
  streakBroken,
  levelUp,
  newLevel,
  questsCompleted,
  gemsEarned,
  heartsRemaining,
  onContinue,
  onReviewLesson,
}: LessonCompleteProps) {
  const [currentScreenIndex, setCurrentScreenIndex] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState<string>('7 days');
  const [streakGoalSet, setStreakGoalSet] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('streakGoalSet') === 'true';
    }
    return false;
  });

  // Determine active screens based on conditions
  const activeScreens = useMemo(() => {
    const screens = ['result', 'xp', 'streak'];
    if (streakDays === 1 && !streakBroken && !streakGoalSet) {
      screens.push('goal');
    }
    if (questsCompleted && questsCompleted.length > 0) {
      screens.push('quests');
    }
    if (gemsEarned > 0) {
      screens.push('gems');
    }
    return screens;
  }, [streakDays, streakBroken, questsCompleted, gemsEarned, streakGoalSet]);

  const currentScreenId = activeScreens[currentScreenIndex];

  // Confetti generator for Screen 0
  const confettiParticles = useMemo(() => {
    const colors = [
      '#FF6B6B', '#FFD93D', '#6BCB77', '#4D96FF', '#FF8AAE',
      '#F59E0B', '#22D3EE', '#34D399', '#F87171', '#A78BFA',
      '#EC4899', '#8B5CF6', '#10B981', '#3B82F6', '#EF4444'
    ];
    return Array.from({ length: 60 }).map((_, i) => {
      const angle = Math.random() * Math.PI * 2;
      const distance = 60 + Math.random() * 140;
      const x = Math.cos(angle) * distance;
      const y = Math.sin(angle) * distance - 20; 
      const size = 4 + Math.floor(Math.random() * 12);
      const isCircle = Math.random() > 0.5;
      const borderRadius = isCircle ? '50%' : `${Math.random() * 4}px`;
      const rotation = Math.random() * 360;
      const targetRotation = rotation + 180 + Math.random() * 360;

      return {
        id: i,
        color: colors[Math.floor(Math.random() * colors.length)],
        x,
        y,
        size,
        borderRadius,
        rotation,
        targetRotation,
        duration: 1.0 + Math.random() * 0.8,
        delay: Math.random() * 0.5,
      };
    });
  }, []);

  // Flame particle generator for Screen 2
  const flameParticles = useMemo(() => {
    return Array.from({ length: 10 }).map((_, i) => ({
      id: i,
      x: -20 + Math.random() * 40,
      yStart: 40,
      yEnd: -60 - Math.random() * 40,
      size: 4 + Math.random() * 6,
      duration: 1.0 + Math.random() * 0.8,
      delay: Math.random() * 1.5,
    }));
  }, []);

  const handleContinue = () => {
    if (currentScreenIndex < activeScreens.length - 1) {
      setCurrentScreenIndex(prev => prev + 1);
    } else {
      onContinue();
    }
  };

  const updateStreakGoal = (days: string) => {
    console.log(`[StreakGoal] User set goal to ${days}`);
  };

  const handleGoalSubmit = () => {
    updateStreakGoal(selectedGoal);
    if (typeof window !== 'undefined') {
      localStorage.setItem('streakGoalSet', 'true');
    }
    setStreakGoalSet(true);
    handleContinue();
  };

  const previousTotalXp = Math.max(0, totalXP - xpEarned);
  const currentLevel = calculateLevel(totalXP);
  const levelStartXp = getTotalXpForLevel(currentLevel);
  const nextLevelXp = getTotalXpForLevel(currentLevel + 1);
  const xpInCurrentLevel = totalXP - levelStartXp;
  const xpNeededForCurrentLevel = nextLevelXp - levelStartXp;
  const initialPercent = levelUp ? 0 : getProgressToNextLevel(previousTotalXp);
  const targetPercent = getProgressToNextLevel(totalXP);

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-between bg-[#0F172A] text-white p-6 md:p-12 overflow-y-auto">
      {/* Animated Screen Wrapper */}
      <div className="flex-1 flex flex-col items-center justify-center max-w-2xl mx-auto w-full my-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentScreenId}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -24 }}
            transition={{ type: "spring", stiffness: 400, damping: 15 }}
            className="w-full flex flex-col items-center text-center space-y-8 py-8"
          >
            {/* SCREEN 0: LESSON RESULT */}
            {currentScreenId === 'result' && (
              <>
                <div className="relative">
                  {/* Confetti burst on lesson complete */}
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    {confettiParticles.map(p => (
                      <motion.div
                        key={p.id}
                        className="absolute"
                        style={{
                          backgroundColor: p.color,
                          width: p.size,
                          height: p.size,
                          borderRadius: p.borderRadius,
                        }}
                        initial={{ x: 0, y: 0, scale: 0, opacity: 1, rotate: p.rotation }}
                        animate={{
                          x: p.x,
                          y: p.y,
                          scale: [0, 1.2, 1, 0],
                          opacity: [1, 1, 0.8, 0],
                          rotate: p.targetRotation,
                        }}
                        transition={{
                          duration: p.duration,
                          delay: p.delay,
                          ease: 'easeOut',
                        }}
                      />
                    ))}
                  </div>
                  {/* Bouncy Happy Atom Avatar */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20 }}
                  >
                    <motion.div
                      animate={{
                        y: [0, -8, 0],
                      }}
                      transition={{
                        duration: 2.0,
                        repeat: Infinity,
                        ease: "easeInOut",
                      }}
                    >
                      <AtomAvatar size={180} state={perfectLesson ? 'celebrating' : accuracy >= 80 ? 'happy' : 'neutral'} />
                    </motion.div>
                  </motion.div>
                </div>

                <div className="space-y-3 overflow-hidden py-2">
                  {/* Heavy overshoot spring drop-in for Title */}
                  <motion.h1
                    initial={{ y: -100, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      type: "spring",
                      stiffness: 400,
                      damping: 15,
                      delay: 0.1,
                    }}
                    className={`text-3xl md:text-4xl font-extrabold tracking-tight ${
                      perfectLesson
                        ? 'text-[#F59E0B] drop-shadow-[0_0_15px_rgba(245,158,11,0.3)]'
                        : accuracy >= 80
                        ? 'text-[#22D3EE]'
                        : 'text-white'
                    }`}
                  >
                    {perfectLesson ? 'Perfect Lesson! ⭐' : accuracy >= 80 ? 'Great Work! 🚀' : 'Lesson Complete'}
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.4 }}
                    className="text-white/60 text-lg"
                  >
                    {perfectLesson ? 'You made no mistakes!' : accuracy >= 80 ? 'Keep it up!' : 'Practice makes perfect'}
                  </motion.p>
                </div>

                <div className="grid grid-cols-3 gap-4 w-full pt-4">
                  {/* Card 1: Pop in with spring bounce */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0, y: 40 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 20,
                      delay: 0.1,
                    }}
                    className="w-full"
                  >
                    <Card className="border-white/10 bg-white/5 text-white rounded-2xl">
                      <CardContent className="p-4 flex flex-col items-center justify-center space-y-1">
                        <span className="text-[10px] md:text-xs tracking-wider font-bold text-amber-400">⚡ XP EARNED</span>
                        <span className="text-xl md:text-2xl font-black text-amber-300"><AnimatedCounter value={xpEarned} /></span>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Card 2: Pop in with spring bounce (staggered delay, different stiffness) */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0, y: 40, rotate: 3 }}
                    animate={{ scale: 1, opacity: 1, y: 0, rotate: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 280,
                      damping: 18,
                      delay: 0.2,
                    }}
                    className="w-full"
                  >
                    <Card className="border-white/10 bg-white/5 text-white rounded-2xl">
                      <CardContent className="p-4 flex flex-col items-center justify-center space-y-1">
                        <span className="text-[10px] md:text-xs tracking-wider font-bold text-emerald-400">🎯 ACCURACY</span>
                        <span
                          className={`text-xl md:text-2xl font-black ${
                            accuracy >= 80 ? 'text-emerald-400' : accuracy >= 50 ? 'text-amber-400' : 'text-rose-500'
                          }`}
                        >
                          {accuracy}%
                        </span>
                      </CardContent>
                    </Card>
                  </motion.div>

                  {/* Card 3: Pop in with spring bounce (staggered delay, different stiffness) */}
                  <motion.div
                    initial={{ scale: 0, opacity: 0, y: 40, rotate: -3 }}
                    animate={{ scale: 1, opacity: 1, y: 0, rotate: 0 }}
                    transition={{
                      type: "spring",
                      stiffness: 260,
                      damping: 16,
                      delay: 0.3,
                    }}
                    className="w-full"
                  >
                    {combo > 0 ? (
                      <Card className="border-white/10 bg-white/5 text-white rounded-2xl">
                        <CardContent className="p-4 flex flex-col items-center justify-center space-y-1">
                          <span className="text-[10px] md:text-xs tracking-wider font-bold text-orange-400">🔥 COMBO</span>
                          <span className="text-xl md:text-2xl font-black text-orange-400">{combo}</span>
                        </CardContent>
                      </Card>
                    ) : (
                      <Card className="border-white/10 bg-white/5 text-white rounded-2xl opacity-40">
                        <CardContent className="p-4 flex flex-col items-center justify-center space-y-1">
                          <span className="text-[10px] md:text-xs tracking-wider font-bold text-slate-400">❤️ HEARTS</span>
                          <span className="text-xl md:text-2xl font-black text-rose-500">{heartsRemaining}/5</span>
                        </CardContent>
                      </Card>
                    )}
                  </motion.div>
                </div>
              </>
            )}

            {/* SCREEN 1: XP & LEVEL */}
            {currentScreenId === 'xp' && (
              <>
                <div className="space-y-4 w-full">
                  <h1 className="text-3xl md:text-4xl font-extrabold text-amber-400 tracking-tight flex items-center justify-center gap-2">
                    ⚡ <AnimatedCounter value={xpEarned} /> XP earned!
                  </h1>
                  
                  {levelUp && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", stiffness: 200, damping: 20 }}
                      className="inline-block bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-900 font-extrabold text-sm md:text-base px-6 py-2 rounded-full shadow-[0_0_20px_rgba(245,158,11,0.5)] border-2 border-white/20"
                    >
                      LEVEL {newLevel} UP! 🎊
                    </motion.div>
                  )}
                </div>

                <div className="w-full bg-slate-800/80 rounded-2xl p-6 border border-white/5 space-y-4 max-w-md mx-auto">
                  <div className="flex justify-between items-center text-xs font-extrabold text-slate-400">
                    <span>LEVEL {currentLevel}</span>
                    <span>{xpInCurrentLevel} / {xpNeededForCurrentLevel} XP</span>
                    <span>LEVEL {currentLevel + 1}</span>
                  </div>

                  <div className="h-4 w-full bg-slate-900 rounded-full overflow-hidden border border-white/10 p-0.5">
                    <motion.div
                      className="h-full bg-gradient-to-r from-amber-400 to-yellow-300 rounded-full"
                      initial={{ width: `${initialPercent}%` }}
                      animate={{ width: `${targetPercent}%` }}
                      transition={{ duration: 1.5, ease: 'easeOut', delay: 0.2 }}
                    />
                  </div>
                  <p className="text-xs text-white/40">{nextLevelXp - totalXP} XP remaining to reach Level {currentLevel + 1}</p>
                </div>

                <div className="w-full flex justify-end pr-4 pt-4">
                  <AtomAvatar size={120} state="happy" className="shadow-[0_0_30px_rgba(96,165,250,0.15)]" />
                </div>
              </>
            )}

            {/* SCREEN 2: STREAK */}
            {currentScreenId === 'streak' && (
              <>
                {streakBroken ? (
                  <>
                    <div className="relative flex items-center justify-center h-40 w-40">
                      {/* Broken Flame representation */}
                      <svg className="w-32 h-32 text-orange-500/20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                        <path d="M12 2C8 6 6 8.5 6 12.5C6 16.5 9 19.5 12 20.5C15 19.5 18 16.5 18 12.5C18 8.5 16 6 12 2Z" />
                        <path d="M8 8L16 16M16 8L8 16" stroke="#EF4444" strokeWidth="2" />
                      </svg>
                    </div>

                    <div className="space-y-3">
                      <h1 className="text-3xl md:text-4xl font-extrabold text-rose-500 tracking-tight">Streak lost 💔</h1>
                      <p className="text-white/60 text-lg">Don't give up — start a new one today</p>
                    </div>
                    <AtomAvatar state="sad" size={140} />
                  </>
                ) : (
                  <>
                    <div className="relative flex items-center justify-center h-48 w-full">
                      {/* Animated Particle floating up */}
                      {flameParticles.map(p => (
                        <motion.div
                          key={p.id}
                          className="absolute rounded-full bg-amber-400"
                          style={{
                            width: p.size,
                            height: p.size,
                            bottom: '10%',
                          }}
                          initial={{ x: p.x, y: p.yStart, opacity: 1, scale: 0.5 }}
                          animate={{
                            y: p.yEnd,
                            opacity: [1, 0.8, 0],
                            scale: [0.5, 1.2, 0.3],
                          }}
                          transition={{
                            duration: p.duration,
                            delay: p.delay,
                            repeat: Infinity,
                            ease: 'easeOut',
                          }}
                        />
                      ))}

                      {/* Flame Vector Illustration */}
                      <motion.svg
                        className="w-36 h-36 drop-shadow-[0_0_25px_rgba(249,115,22,0.4)]"
                        viewBox="0 0 100 120"
                        animate={{
                          scaleY: [1, 1.06, 0.98, 1.04, 1],
                          scaleX: [1, 0.96, 1.03, 0.97, 1],
                        }}
                        transition={{
                          duration: 2.0,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <defs>
                          <linearGradient id="flameGrad" x1="0" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#EA580C" />
                            <stop offset="60%" stopColor="#F97316" />
                            <stop offset="100%" stopColor="#FBBF24" />
                          </linearGradient>
                        </defs>
                        <path
                          d="M 50 10 Q 15 65 35 95 C 45 110 55 110 65 95 Q 85 65 50 10 Z"
                          fill="url(#flameGrad)"
                        />
                        <path
                          d="M 50 30 Q 30 75 42 90 C 48 98 52 98 58 90 Q 70 75 50 30 Z"
                          fill="#FDE047"
                          opacity="0.8"
                        />
                      </motion.svg>
                    </div>

                    <motion.div
                      initial={{ scale: 0.3, opacity: 0 }}
                      animate={{ 
                        scale: [0.3, 1.25, 0.95, 1.05, 1], 
                        opacity: 1 
                      }}
                      transition={{ 
                        duration: 0.8, 
                        ease: "easeOut",
                        type: "spring",
                        stiffness: 120,
                        damping: 10
                      }}
                      className="space-y-3"
                    >
                      <h1 className="text-3xl md:text-4xl font-extrabold text-[#F97316] tracking-tight flex items-center justify-center gap-2">
                        🔥 <motion.span
                          className="inline-block"
                          animate={{
                            scale: [1, 1.2, 1],
                            rotate: [0, 5, -5, 0],
                          }}
                          transition={{
                            duration: 1.5,
                            repeat: Infinity,
                            repeatType: "reverse",
                            ease: "easeInOut",
                          }}
                        >{streakDays}</motion.span> Day Streak!
                      </h1>
                      <p className="text-white/60 text-lg">
                        {streakDays === 1 ? "You're on a roll!" : streakDays >= 30 ? "Unstoppable! 🏆" : "Incredible consistency!"}
                      </p>
                    </motion.div>
                    <AtomAvatar state="excited" size={140} className="shadow-[0_0_30px_rgba(249,115,22,0.15)]" />
                  </>
                )}
              </>
            )}

            {/* SCREEN 3: STREAK GOAL */}
            {currentScreenId === 'goal' && (
              <>
                <AtomAvatar state="thinking" size={120} />

                <div className="space-y-2">
                  <h1 className="text-2xl md:text-3xl font-extrabold tracking-tight">Set a Streak Goal! 🎯</h1>
                  <p className="text-white/60 text-base">Commit to learning every day</p>
                </div>

                <div className="grid grid-cols-2 gap-4 w-full max-w-md">
                  {[
                    { days: '7 days', label: 'Good' },
                    { days: '14 days', label: 'Great' },
                    { days: '30 days', label: 'Incredible' },
                    { days: '50 days', label: 'Unstoppable' },
                  ].map(g => (
                    <button
                      key={g.days}
                      type="button"
                      onClick={() => setSelectedGoal(g.days)}
                      className={`flex flex-col items-center justify-center p-4 rounded-2xl border-2 transition-all cursor-pointer ${
                        selectedGoal === g.days
                          ? 'border-cyan-400 bg-cyan-400/10 text-white shadow-[0_0_15px_rgba(34,211,238,0.2)]'
                          : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
                      }`}
                    >
                      <span className="font-bold text-lg">{g.days}</span>
                      <span className="text-xs uppercase tracking-wider text-cyan-400 font-bold opacity-80 mt-1">{g.label}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* SCREEN 4: QUESTS */}
            {currentScreenId === 'quests' && (
              <>
                <div className="space-y-2">
                  <h1 className="text-2xl md:text-3xl font-extrabold text-[#F59E0B] tracking-tight">
                    {questsCompleted.length} Daily Quest{questsCompleted.length > 1 ? 's' : ''} Complete! 🏆
                  </h1>
                </div>

                <div className="space-y-4 w-full max-w-md">
                  {questsCompleted.map((q, idx) => (
                    <Card key={q.id || idx} className="border-white/10 bg-white/5 text-white rounded-2xl overflow-hidden">
                      <CardContent className="p-5 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-xl bg-amber-400/10 flex items-center justify-center text-amber-400 flex-shrink-0">
                            {q.id.includes('perfect') ? <Award className="w-5 h-5" /> : <Flame className="w-5 h-5" />}
                          </div>
                          <div className="text-left space-y-1">
                            <p className="font-bold text-sm leading-tight text-white/95">{q.title}</p>
                            {/* Animated Quest Progress bar */}
                            <div className="h-2 w-36 md:w-48 bg-slate-900 rounded-full overflow-hidden border border-white/5">
                              <motion.div
                                className="h-full bg-amber-400 rounded-full"
                                initial={{ width: '0%' }}
                                animate={{ width: '100%' }}
                                transition={{ duration: 1.0, ease: 'easeOut', delay: 0.1 * idx }}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="bg-amber-400/15 border border-amber-400/30 rounded-xl px-3 py-1.5 flex items-center gap-1.5 text-xs text-amber-300 font-black flex-shrink-0">
                          💎 {q.reward}
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                  {gemsEarned > 0 && (
                    <div className="text-sm font-extrabold text-cyan-400 tracking-wide pt-2">
                      💎 +{gemsEarned} GEMS EARNED TOTAL
                    </div>
                  )}
                </div>
              </>
            )}

            {/* SCREEN 5: GEMS */}
            {currentScreenId === 'gems' && (
              <>
                <div className="relative flex items-center justify-center h-48 w-full">
                  {/* Chest Vector Illustration */}
                  <div className="relative flex flex-col items-center">
                    {/* Chest Top Lid */}
                    <div className="w-40 h-14 bg-[#78350F] rounded-t-3xl border-2 border-[#92400E] shadow-2xl relative z-10 flex items-end justify-center">
                      {/* Gem details inside overlapping top lid */}
                      <div className="absolute bottom-[-6px] flex gap-1 justify-center w-full z-20">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-4 h-4 bg-cyan-400 rotate-45 border border-white shadow-[0_0_10px_#22D3EE]"
                            animate={{
                              y: [0, -3, 0],
                              scale: [1, 1.1, 1],
                            }}
                            transition={{
                              duration: 1.5 + i * 0.3,
                              repeat: Infinity,
                              ease: 'easeInOut',
                            }}
                          />
                        ))}
                      </div>
                      <div className="w-6 h-6 bg-[#D97706] rounded-b-lg border-x-2 border-b-2 border-[#B45309]" />
                    </div>

                    {/* Chest Bottom Base */}
                    <div className="w-40 h-20 bg-[#92400E] rounded-b-2xl border-x-2 border-b-2 border-[#78350F] shadow-2xl flex items-start justify-center relative">
                      <div className="w-6 h-4 bg-[#F59E0B] border-x border-b border-[#D97706]" />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                    You earned {gemsEarned} gems! 💎
                  </h1>
                  <p className="text-white/60 text-sm max-w-sm">
                    Complete Daily Quests every day to earn more
                  </p>
                </div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer controls container */}
      <footer className="w-full max-w-2xl mx-auto flex items-center justify-between pt-6 border-t border-white/10 mt-auto">
        <div className="flex-1">
          {/* Review Lesson Button visible on screens 0 and 1 only */}
          {(currentScreenId === 'result' || currentScreenId === 'xp') ? (
            <Button
              type="button"
              variant="outline"
              onClick={onReviewLesson}
              className="border border-white/15 bg-white/5 text-white/80 hover:bg-white/10 hover:text-white font-bold px-6 py-4 rounded-xl transition-all"
            >
              Review Lesson
            </Button>
          ) : (
            <div className="w-10" />
          )}
        </div>

        <div className="flex-1 flex justify-end">
          {currentScreenId === 'goal' ? (
            <Button
              type="button"
              onClick={handleGoalSubmit}
              className="bg-cyan-500 hover:bg-cyan-600 text-white font-bold px-8 py-4 rounded-xl shadow-lg transition-all"
            >
              I Can Do It!
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleContinue}
              aria-label="Continue"
              className="bg-gradient-to-r from-cyan-400 to-fuchsia-500 hover:opacity-95 text-white font-bold px-8 py-4 rounded-xl shadow-lg transition-all flex items-center gap-1.5"
            >
              <span>Continue</span>
              <ChevronRight className="w-4 h-4" aria-hidden="true" />
            </Button>
          )}
        </div>
      </footer>
    </div>
  );
}
